import {
  GeoJSONFeature
} from 'maplibre-gl';

import { geojsonTypes, cursors } from './utils/constants';
import doubleClickZoom from './utils/double_click_zoom';
import DrawPoint from './utils/draw_point';

import {
  calculateSnapTolerance,
  clearSnapMarkerOnMap,
  sanitizeSnapOptions,
  setSnapMarkerOnMap,
  type SnapHitKind,
} from './utils/CalculateSnapTolerance';
import { positionToLngLat } from '../../Controls/featureCreateGeometry';

import { Source, drawState } from '../../Types';

const SnapPointMode = { ...DrawPoint };

export default SnapPointMode;

SnapPointMode.onSetup = function (options:any) {

  const point = this.newFeature({
    type: geojsonTypes.FEATURE,
    properties: {},
    geometry: {
      type: geojsonTypes.POINT,
      coordinates: [],
    },
  });

  this.addFeature(point);

  const selectedFeatures = this.getSelected();
  this.clearSelectedFeatures();
  doubleClickZoom.disable(this);

  const state:drawState = <drawState>{
    map: this.map,
    point,
    selectedFeatures,
  };

  state.options = sanitizeSnapOptions(this._ctx.options);

  const optionsChangedCallBAck = (options:drawState["options"]) => {
    state.options = sanitizeSnapOptions(options);
  };

  state.optionsChangedCallBAck = optionsChangedCallBAck;

  this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

  this.snapMarkerSource = (<Source>this.map.getSource('geobox-gl-snap-marker'))._data;

  const center = this.map.getCenter();
  state.snappedLng = center.lng;
  state.snappedLat = center.lat;

  return state;
};

SnapPointMode.onTap = function (state: drawState, e: any) {
  SnapPointMode.onMouseMove.call(this, state, e);
  SnapPointMode.onClick.call(this, state, e);
};

/** Place point at snapped WGS84 lng/lat when locked; otherwise at cursor. */
SnapPointMode.onClick = function (state: drawState, e?: any) {
  if (e) {
    SnapPointMode.onMouseMove.call(this, state, e);
  }
  const lng = state.snappedLng;
  const lat = state.snappedLat;

  DrawPoint.onClick.call(this, state, {
    lngLat: { lng, lat },
  });
};

SnapPointMode.onMouseMove = function (state:drawState, e:any) {
  const { lng, lat } = e.lngLat;
  let minDistance = Infinity;
  let desiredCoord: number[] | undefined;
  let snapKind: SnapHitKind | undefined;

  if (state.options.snap) {
    [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(this.map, e, state);
  }

  const locked = Boolean(
    state.options.snap &&
      desiredCoord &&
      Number.isFinite(minDistance) &&
      minDistance <= (state.options.snapTolerance ?? Infinity)
  );

  if (locked && desiredCoord) {
    const [snapLng, snapLat] = positionToLngLat(desiredCoord as [number, number]);
    state.snappedLng = snapLng;
    state.snappedLat = snapLat;
  } else {
    state.snappedLng = lng;
    state.snappedLat = lat;
  }

  if (state.options.snap) {
    setSnapMarkerOnMap(this.map, state.snappedLng, state.snappedLat, {
      locked,
      kind: locked ? snapKind ?? null : null,
    });
  } else {
    clearSnapMarkerOnMap(this.map);
  }

  if (
    state.lastVertex
    && state.lastVertex[0] === lng
    && state.lastVertex[1] === lat
  ) {
    this.updateUIClasses({ mouse: cursors.POINTER });
  } else {
    this.updateUIClasses({ mouse: cursors.ADD });
  }
};

// This is 'extending' DrawPoint.toDisplayFeatures
SnapPointMode.toDisplayFeatures = function (state:drawState, geojson:GeoJSONFeature, display:any) {
  DrawPoint.toDisplayFeatures(state, geojson, display);
};

SnapPointMode.onStop = function (state:drawState) {
  try {
    if (state?.optionsChangedCallBAck) {
      (this.map as any).off('draw.snap.options_changed', state.optionsChangedCallBAck);
    }
  } catch {
    /* ignore */
  }
  clearSnapMarkerOnMap(this.map);
  this.map.getCanvasContainer().style.cursor = "grab";
  
  DrawPoint.onStop.call(this, state);
};

// export default SnapPointMode;
